import { useCallback, useState } from "react";
import {
  Alert,
  Card,
  Col,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faPlus,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Button from "components/base/Button";
import CommandPalette from "components/common/CommandPalette";
import LinkProduto from "components/common/LinkProduto";
import { useBuscarProdutosNFe } from "hooks/useNFe";
import { BuscarProdutosNFeItem } from "types/nfe";
import { KitFormValues, KitComponenteFormValue } from "pages/retaguarda/kitForm";

// ─── Helpers ───────────────────────────────────────────────────────────────────

const formatCurrency = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const TOLERANCE = 0.01;

// ─── Estado da linha de adição ──────────────────────────────────────────────────

interface LinhaState {
  produto: BuscarProdutosNFeItem | null;
  quantidade: number;
}

const linhaInicial: LinhaState = { produto: null, quantidade: 1 };

// ─── Componente ────────────────────────────────────────────────────────────────

const ComponentesKitCard = () => {
  const { control, setValue, formState: { errors } } = useFormContext<KitFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "componentes",
  });

  const precoKit = useWatch({ control, name: "precoKit" }) ?? 0;
  const componentes = useWatch({ control, name: "componentes" }) ?? [];

  // Estado da linha de adição
  const [linha, setLinha] = useState<LinhaState>(linhaInicial);
  const [showBusca, setShowBusca] = useState(false);
  const [termoBusca, setTermoBusca] = useState("");

  const queryBusca = useBuscarProdutosNFe(termoBusca, showBusca && termoBusca.length >= 2);

  const handleSearch = useCallback((t: string) => setTermoBusca(t), []);

  const handleSelecionarProduto = useCallback((produto: BuscarProdutosNFeItem) => {
    setLinha((prev) => ({ ...prev, produto }));
  }, []);

  const handleAdicionar = () => {
    if (!linha.produto) return;
    const qtd = linha.quantidade > 0 ? linha.quantidade : 1;
    append({
      componenteProdutoId: linha.produto.id,
      produtoCodigo: linha.produto.codigo,
      produtoDescricao: linha.produto.descricao,
      quantidade: qtd,
      precoNoKit: 0,
      custoUnitario: 0,
      vendaUnitario: linha.produto.valor,
    });
    setLinha(linhaInicial);
  };

  // Rateio proporcional ao total de venda de cada componente
  const handleRatear = () => {
    const totalVenda = componentes.reduce(
      (acc, c) => acc + (c.vendaUnitario ?? 0) * (c.quantidade ?? 1),
      0
    );
    if (totalVenda <= 0 || precoKit <= 0) return;

    componentes.forEach((c, index) => {
      const totalLinha = (c.vendaUnitario ?? 0) * (c.quantidade ?? 1);
      const proporcao = totalLinha / totalVenda;
      const precoRateado = Math.round(precoKit * proporcao * 100) / 100;
      setValue(`componentes.${index}.precoNoKit`, precoRateado, { shouldDirty: true });
    });
  };

  // Totais do rodapé
  const totalCusto = componentes.reduce(
    (acc, c) => acc + (c.custoUnitario ?? 0) * (c.quantidade ?? 1),
    0
  );
  const totalVenda = componentes.reduce(
    (acc, c) => acc + (c.vendaUnitario ?? 0) * (c.quantidade ?? 1),
    0
  );
  const somaPrecoNoKit = componentes.reduce(
    (acc, c) => acc + (c.precoNoKit ?? 0),
    0
  );
  const divergePreco =
    componentes.length > 0 &&
    precoKit > 0 &&
    Math.abs(somaPrecoNoKit - precoKit) > TOLERANCE;

  return (
    <>
      <Card className="mb-4">
        <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Componentes do Kit</h5>
          {fields.length > 0 && (
            <Button
              variant="phoenix-secondary"
              size="sm"
              onClick={handleRatear}
              type="button"
              title="Distribuir o Preço do Kit proporcionalmente entre os componentes"
            >
              Ratear Preço do Kit
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {/* Aviso de divergência */}
          {divergePreco && (
            <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-3">
              <div className="text-warning-emphasis small">
                A soma dos preços no kit ({formatCurrency(somaPrecoNoKit)}) difere do Preço do Kit ({formatCurrency(precoKit)}).
                Ajuste os valores ou use "Ratear Preço do Kit".
              </div>
            </Alert>
          )}

          {/* Linha de adição */}
          <Row className="g-2 align-items-end mb-3">
            {/* Busca de produto */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Produto</Form.Label>
                <div
                  role="button"
                  onClick={() => setShowBusca(true)}
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className={`text-truncate ${
                      linha.produto ? "text-body-emphasis" : "text-body-tertiary"
                    }`}
                  >
                    {linha.produto
                      ? `${linha.produto.codigo} — ${linha.produto.descricao}`
                      : "Clique para buscar um produto…"}
                  </span>
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-body-tertiary ms-2 flex-shrink-0"
                  />
                </div>
              </Form.Group>
            </Col>

            {/* Quantidade */}
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label>Quantidade</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  min="1"
                  value={linha.quantidade}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) =>
                    setLinha((prev) => ({
                      ...prev,
                      quantidade: parseInt(e.target.value) || 1,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            {/* Botão adicionar */}
            <Col xs="auto">
              <Button
                variant="primary"
                onClick={handleAdicionar}
                disabled={!linha.produto}
                type="button"
                title="Adicionar componente"
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Adicionar
              </Button>
            </Col>
          </Row>

          {/* Tabela de componentes */}
          {fields.length === 0 ? (
            <div className="text-center py-5 px-3">
              <FontAwesomeIcon
                icon={faBoxOpen}
                className="text-body-tertiary mb-3"
                style={{ fontSize: "2.5rem" }}
              />
              <h6 className="text-body-secondary">Nenhum componente adicionado</h6>
              <p className="text-body-tertiary small mb-0">
                Busque e adicione produtos para compor o kit.
              </p>
            </div>
          ) : (
            <>
              <Table responsive className="phoenix-table fs-9 mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Código</th>
                    <th>Produto</th>
                    <th style={{ width: 90 }}>Qtd</th>
                    <th style={{ width: 120 }}>Total Custo</th>
                    <th style={{ width: 120 }}>Total Venda</th>
                    <th style={{ width: 140 }}>Preço no Kit</th>
                    <th style={{ width: 56 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const comp = componentes[index] as KitComponenteFormValue | undefined;
                    const qtd = comp?.quantidade ?? field.quantidade ?? 1;
                    const totalCustoLinha = (comp?.custoUnitario ?? field.custoUnitario ?? 0) * qtd;
                    const totalVendaLinha = (comp?.vendaUnitario ?? field.vendaUnitario ?? 0) * qtd;

                    return (
                      <tr key={field.id}>
                        <td className="align-middle text-body-secondary">
                          #{field.produtoCodigo}
                        </td>
                        <td className="align-middle">
                          <LinkProduto
                            produtoId={field.componenteProdutoId}
                            className="fw-semibold text-body-emphasis"
                          >
                            {field.produtoDescricao}
                          </LinkProduto>
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="number"
                            step="1"
                            min="1"
                            size="sm"
                            style={{ width: 70 }}
                            defaultValue={field.quantidade}
                            onFocus={(e) => e.currentTarget.select()}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              setValue(`componentes.${index}.quantidade`, val, { shouldDirty: true });
                            }}
                          />
                        </td>
                        <td className="align-middle text-body-secondary">
                          {formatCurrency(totalCustoLinha)}
                        </td>
                        <td className="align-middle text-body-secondary">
                          {formatCurrency(totalVendaLinha)}
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            size="sm"
                            style={{ width: 110 }}
                            defaultValue={field.precoNoKit}
                            onFocus={(e) => e.currentTarget.select()}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setValue(`componentes.${index}.precoNoKit`, val, { shouldDirty: true });
                            }}
                          />
                        </td>
                        <td className="align-middle">
                          <Button
                            variant="phoenix-danger"
                            size="sm"
                            className="px-2"
                            onClick={() => remove(index)}
                            type="button"
                            title="Remover componente"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Rodapé com totais */}
                <tfoot className="border-top">
                  <tr className="fw-semibold text-body-secondary">
                    <td colSpan={3} className="text-end pe-3 pt-2">Totais:</td>
                    <td className="pt-2">{formatCurrency(totalCusto)}</td>
                    <td className="pt-2">{formatCurrency(totalVenda)}</td>
                    <td className={`pt-2 ${divergePreco ? "text-warning-emphasis fw-bold" : ""}`}>
                      {formatCurrency(somaPrecoNoKit)}
                      {divergePreco && (
                        <div className="text-body-tertiary fw-normal" style={{ fontSize: "0.75rem" }}>
                          kit: {formatCurrency(precoKit)}
                        </div>
                      )}
                    </td>
                    <td className="pt-2"></td>
                  </tr>
                </tfoot>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {/* CommandPalette de busca de produto */}
      <CommandPalette
        show={showBusca}
        onHide={() => setShowBusca(false)}
        onSearch={handleSearch}
        items={queryBusca.data ?? []}
        isFetching={queryBusca.isFetching}
        isSuccess={queryBusca.isSuccess}
        onSelect={handleSelecionarProduto}
        getKey={(p) => p.id}
        placeholder="Código, descrição ou EAN..."
        emptyMessage="Nenhum produto encontrado"
        minChars={2}
        renderRow={(produto) => (
          <>
            <span className="cmd-row__doc">{produto.codigo}</span>
            <span className="cmd-row__name">{produto.descricao}</span>
            {produto.unidade && (
              <span
                style={{
                  fontSize: "0.68rem",
                  color: "var(--cmd-muted)",
                  flexShrink: 0,
                }}
              >
                {produto.unidade}
              </span>
            )}
          </>
        )}
      />
    </>
  );
};

export default ComponentesKitCard;
